import CancelIcon from "@mui/icons-material/Cancel";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";

import {
  Coupon,
  CreditsInfo,
  HubPlan,
  PlanInfo,
  Subscription,
  UpdateSubscription,
} from "data/models";
import {
  ReactQueryMutationError,
  cancelFutureSubscription,
  scheduleCancellation,
  undoScheduledCancellation,
  updateSubscription,
  validateCoupon,
} from "data/mutations";
import {
  fetchCredits,
  fetchOrganisation,
  fetchPaymentMethod,
  fetchPlans,
  fetchSubscription,
} from "data/queries";
import dayjs from "dayjs";
import { CheckCircleIcon } from "lucide-react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { roundDownOverageBudget } from "utils/calculations";
import { toTitleCase } from "utils/commons";
import { formatPrice } from "utils/format";
import { resolveIdentityId } from "utils/identity";
import { ConfirmChangesSection } from "./confirm-changes";
import CreditUseModal from "./credit-use-modal";
import FeatureComparisonModal from "./feature-comparison-modal";
import { ProductCard } from "./product-card";

type ChangeItem = {
  lhs: string;
  rhs?: string;
  hidden?: boolean;
};

export type ChangeSummary = {
  infoText: string;
  changeType: ChangeType;
  subscription: ChangeItem[];
  charges: ChangeItem[];
  totalDue: number | string;
  trialChargeWarning: string | null;
};

type ChangeType = "upgrade" | "downgrade" | "same" | "no_selection";

const formatDate = (dt: dayjs.Dayjs) => {
  return dt.format("D MMM YYYY");
};

const getPlan = (
  productKey: string,
  cadence: "month" | "year",
  planInfo: PlanInfo
) => {
  const validPrices = planInfo.availablePlans.filter(
    (x) => !x.deprecated && !x.externally_managed
  );

  const productPrices = validPrices.filter((x) => x.product_key === productKey);

  if (!productPrices) {
    return null;
  }
  const matchingPrice = productPrices.filter((x) => x.cadence === cadence);

  if (!matchingPrice) {
    return null;
  }
  return matchingPrice[0];
};

const getSuccessText = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null
) => {
  if (selectedPlan === null) {
    return "No new plan selected";
  }
  if (currentPlan === null) {
    return `Successfully started a ${selectedPlan.display_name} subscription!`;
  }
  if (currentPlan.tier > selectedPlan.tier) {
    return `Successfully scheduled downgrade from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
  }
  return `Successfully upgradaded from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
};

const getChangeType = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null,
  onAdminTrial: boolean = false
): ChangeType => {
  if (selectedPlan === null) {
    return "no_selection";
  }
  if (currentPlan === null) {
    return "upgrade";
  }
  if (selectedPlan.tier === currentPlan.tier && !onAdminTrial) {
    return "same";
  }
  if (currentPlan.tier > selectedPlan.tier) {
    return "downgrade";
  }

  return "upgrade";
};

export const ManageSubscription = () => {
  const [annualPlans, setAnnualPlans] = useState<boolean>(false);
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<HubPlan | null>(null);
  const [overageInput, setOverageInput] = useState(0);
  const [newOverageBudget, setNewOverageBudget] = useState(0);
  const [featureComparisonModalOpen, setFeatureComparisonModalOpen] =
    useState<boolean>(false);
  const [creditUseModalOpen, setCreditUseModalOpen] = useState<boolean>(false);

  const plansQuery = useQuery([QueryKeys.plans], () => fetchPlans());
  const [identityState, _] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const [undoCancelModalIsOpen, setUndoCancelModalIsOpen] = useState(false);
  const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
  const [trialSelected, setTrialSelected] = useState(false);

  const subscriptionQuery = useQuery<Subscription>(
    [QueryKeys.subscription, identityId],
    () => fetchSubscription()
  );
  const paymentMethodQuery = useQuery(
    [QueryKeys.paymentMethod, identityId],
    () => fetchPaymentMethod()
  );
  const creditsQuery = useQuery(
    [QueryKeys.overage, identityId],
    () => fetchCredits(),
    {
      onSuccess: (result: CreditsInfo) => {
        if (result && result.overageDollarsLimit) {
          setOverageInput(result.overageDollarsLimit);
        }
      },
    }
  );

  const newPlanPermissionKeys = selectedPlan
    ? trialSelected
      ? [selectedPlan.product_key, "free_trial"]
      : [selectedPlan.product_key]
    : undefined;

  const { isPermitted: newPlanIsPermitted, isPermissionsLoading } =
    usePermissions({
      permissionSets: newPlanPermissionKeys,
    });

  // Check permissions for the scheduled future subscription plan
  const nextSubPermissionKeys = subscriptionQuery.data?.activeSubscription
    ?.nextSub
    ? [subscriptionQuery.data.activeSubscription.nextSub.plan.product_key]
    : undefined;

  const { isPermitted: nextSubIsPermitted } = usePermissions({
    permissionSets: nextSubPermissionKeys,
  });

  const queryClient = useQueryClient();
  const membershipsQuery = useQuery([identityState, QueryKeys.userMembership]);
  const user = identityState as User;

  const orgMembershipsQuery = useQuery([
    QueryKeys.userMembership,
    user.cognitoSub,
  ]);

  const organisationQuery = useQuery([QueryKeys.organisation, identityId], () =>
    fetchOrganisation()
  );

  const subscriptionMutation = useMutation(
    (values: UpdateSubscription) => updateSubscription(values),
    {
      onSuccess: (updatedSubscription) => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: updatedSubscription.activeSubscription,
        });

        membershipsQuery.refetch();
        orgMembershipsQuery.refetch();
        // queryClient.setQueryData([QueryKeys.overage, identityId], {
        //   ...overageQuery.data,
        //   dollarsLimit: updatedSubscription.overageLimitDollars,
        // });

        enqueueSnackbar(
          getSuccessText(
            plansQuery.data?.availablePlans.find(
              (x) => x.price_lookup_id === selectedPrice
            ) ?? null,
            subscriptionQuery.data?.activeSubscription?.plan ?? null
          ),
          {
            variant: "success",
          }
        );

        // Redirect
        history.push(Routes.developerDashboard);
      },
    }
  );
  const currentPlan = subscriptionQuery.data?.activeSubscription?.plan ?? null;

  const getChangeSummary = (): ChangeSummary => {
    const startDateDt =
      changeType === "upgrade"
        ? dayjs()
        : dayjs(
            (subscriptionQuery.data?.activeSubscription?.renewalDate ?? 1) *
              1000
          );
    const renewalDate = startDateDt.add(1, selectedPlan?.cadence);
    let infoText = "";
    if (changeType === "upgrade") {
      infoText =
        "These changes will apply immediately, and you will be charged the total due below";
    } else if (changeType === "downgrade") {
      infoText =
        "These changes will take effect at the end of your current billing cycle. The due amount will be charged on the renewal date, along with any additional overage incurred";
    } else {
      return {
        infoText: "No plan selected",
        changeType: changeType,
        subscription: [],
        charges: [],
        totalDue: 0,
        trialChargeWarning: null,
      };
    }

    let couponText = "";
    if (couponValid) {
      if (couponMutation.data?.duration === "once") {
        couponText = `(First ${selectedPlan?.cadence})`;
      } else {
        couponText = "(Repeating)";
      }
    }
    const planAmountDue = trialSelected ? 0 : selectedPlan?.dollar_cost ?? 0;

    return {
      infoText: infoText,
      changeType: changeType,
      subscription: [
        { lhs: `Subscribe to ${selectedPlan?.display_name}` },
        { lhs: `Start Date`, rhs: formatDate(startDateDt) },
        { lhs: `Automatically Renews`, rhs: formatDate(renewalDate) },
        {
          lhs: `Starting Credit Balance`,
          rhs: `${selectedPlan?.credit_amount.toLocaleString()} credits`,
        },
        { lhs: "Overage Budget", rhs: `Up to $${newOverageBudget}` },
      ],
      charges: [
        {
          lhs: "Previous Subscription Overage",
          rhs: formatPrice(creditsQuery.data?.overageDollarsSpent ?? 0),
        },
        {
          lhs: trialSelected
            ? `${selectedPlan?.display_name} Subscription - Trial `
            : `${selectedPlan?.display_name} Subscription`,
          rhs: formatPrice(planAmountDue),
        },
        {
          lhs: couponMutation.data?.name ?? "",
          rhs: `-$${calculateCouponDiscount(
            selectedPlan?.dollar_cost ?? 0,
            selectedPlan?.price_lookup_id ?? "",
            couponMutation.data
          )}`,
          hidden: !couponValid,
        },
        {
          lhs: "GST (10% Inclusive)",
          rhs: formatPrice(
            (planAmountDue + (creditsQuery.data?.overageDollarsSpent ?? 0)) / 11
          ),
        },
      ],
      totalDue: newPlanHeader(planAmountDue),
      trialChargeWarning: trialSelected
        ? `Your trial automatically converts to a paid plan at ${formatPrice(
            selectedPlan?.dollar_cost ?? 0
          )}/${selectedPlan?.cadence} on the ${formatDate(
            renewalDate
          )}, unless cancelled prior`
        : null,
    };
  };

  const isSuccess = plansQuery.isSuccess;
  const handlePriceSelection = (selected: string) => {
    setSelectedPrice(selected);
    if (plansQuery.data)
      setSelectedPlan(
        getPlanByPriceLookupId(selected, plansQuery.data.availablePlans)
      );
  };

  const getPlanByPriceLookupId = (priceLookupId: string, plans: HubPlan[]) => {
    return plans.find((plan) => plan.price_lookup_id === priceLookupId) ?? null;
  };

  const roundedBudget = roundDownOverageBudget(overageInput, {
    overageRate: selectedPlan?.overage_cost_dollars!,
    planCredits: selectedPlan?.credit_amount ?? 0,
  });

  const [coupon, setCoupon] = useState("");

  const undoScheduledCancellationMutation = useMutation(
    () => undoScheduledCancellation(),
    {
      onSuccess: () => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: {
            ...subscriptionQuery.data?.activeSubscription,
            cancelAtPeriodEnd: false,
          },
        });
        setUndoCancelModalIsOpen(false);
        enqueueSnackbar(
          "You successfully cancelled your pending subscription cancellation.",
          {
            variant: "success",
          }
        );
      },
    }
  );

  const futureSubscriptionMutation = useMutation(
    () => cancelFutureSubscription(),
    {
      onSuccess: () => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: {
            ...subscriptionQuery.data?.activeSubscription,
            nextSub: undefined,
          },
        });
        setCancelModalIsOpen(false);
        enqueueSnackbar(
          "You successfully cancelled your pending subscription downgrade.",
          {
            variant: "success",
          }
        );
      },
    }
  );

  const scheduleCancellationMutation = useMutation(
    () => scheduleCancellation(),
    {
      onSuccess: () => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: {
            ...subscriptionQuery.data?.activeSubscription,
            cancelAtPeriodEnd: true,
          },
        });
        setCancelModalIsOpen(false);
        enqueueSnackbar(
          "You successfully scheduled a subscription cancellation.",
          {
            variant: "success",
          }
        );
      },
    }
  );

  const couponMutation = useMutation((promoCode: string) =>
    validateCoupon(promoCode)
  );

  const calculateCouponDiscount = (
    subscription_price: number,
    priceKey: string,
    coupon?: Coupon
  ): number => {
    if (coupon) {
      if (isCouponValid(priceKey, coupon)) {
        if (coupon.amount_off) return coupon.amount_off / 100;
        if (coupon.percent_off)
          return (subscription_price * coupon.percent_off) / 100;
      }
    }
    return 0;
  };

  const generateCouponMessage = (coupon: Coupon) => {
    const discount = coupon.amount_off
      ? `$${coupon.amount_off / 100}`
      : `${coupon.percent_off}%`;
    return `${discount} discount has been applied. Duration: ${coupon.duration}`;
  };

  const isCouponValid = (priceKey: string, coupon?: Coupon): boolean => {
    return (
      !!coupon &&
      !!coupon.applies_to &&
      coupon.applies_to.price_keys.includes(priceKey)
    );
  };

  function newPlanHeader(planAmountDue: number) {
    if (selectedPlan) {
      return formatPrice(
        (creditsQuery.data?.overageDollarsSpent ?? 0) +
          planAmountDue -
          Math.min(
            calculateCouponDiscount(
              selectedPlan.dollar_cost,
              selectedPlan.price_lookup_id,
              couponMutation.data
            ),
            planAmountDue
          )
      );
    }
    return "";
  }

  const couponValid = isCouponValid(
    selectedPlan?.price_lookup_id ?? "",
    couponMutation.data
  );

  const handleCancel = () => {
    setSelectedPrice(null);
    setSelectedPlan(null);
    setCoupon("");
    setOverageInput(creditsQuery.data?.overageDollarsLimit ?? overageInput);
    setNewOverageBudget(0);
    couponMutation.reset();
  };

  const hasScheduledCancellation = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.cancelAtPeriodEnd === true
    : true;

  const hasScheduledDowngrade = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.nextSub != null
    : true;

  const onUserInitiatedTrial =
    subscriptionQuery.data &&
    subscriptionQuery.data.activeSubscription?.trialType == "user";

  const onAdminInitiatedTrial =
    subscriptionQuery.data &&
    subscriptionQuery.data.activeSubscription?.trialType == "admin";

  const onTrial = onUserInitiatedTrial || onAdminInitiatedTrial;

  const blockedForPaymentMethodNeeded = !(
    paymentMethodQuery.data?.active || selectedPlan?.free_plan
  );

  const changeTypeAllowed =
    selectedPlan &&
    (selectedPlan.tier !==
      subscriptionQuery.data?.activeSubscription?.plan.tier ||
      onTrial); // subbing to same tier is allowed in the case of going trial -> full sub

  const canContinue = !!(
    changeTypeAllowed &&
    !blockedForPaymentMethodNeeded &&
    (!hasScheduledCancellation || onAdminInitiatedTrial) &&
    !hasScheduledDowngrade
  );

  const canConfigureOverage = newPlanIsPermitted([
    PermissionsKeys.overageUpdate,
  ]);

  const canDoTrial =
    organisationQuery.isSuccess && !organisationQuery.data.hasUsedTrial;

  const changeType = getChangeType(selectedPlan, currentPlan, onTrial);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ReactQueryErrorWrapper
        queries={[
          subscriptionQuery,
          paymentMethodQuery,
          plansQuery,
          creditsQuery,
        ]}
        mutations={[
          subscriptionMutation,
          undoScheduledCancellationMutation,
          scheduleCancellationMutation,
          couponMutation,
        ]}
      />
      {subscriptionQuery.isSuccess &&
        isSuccess &&
        subscriptionQuery.data.activeSubscription && (
          <>
            {hasScheduledCancellation && !onAdminInitiatedTrial && (
              <>
                <Alert
                  severity="info"
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        undoScheduledCancellationMutation.reset();
                        setUndoCancelModalIsOpen(true);
                      }}
                    >
                      Cancel scheduled cancellation
                    </Button>
                  }
                >
                  <AlertTitle>Scheduled plan cancellation</AlertTitle>
                  You have a scheduled cancellation to your{" "}
                  {
                    subscriptionQuery.data.activeSubscription.plan.display_name
                  }{" "}
                  subscription, that will take effect on the{" "}
                  {formatTimestamp(
                    subscriptionQuery.data.activeSubscription.renewalDate * 1000
                  )}
                  . Until then you are still able to use all of the features of{" "}
                  {toTitleCase(
                    subscriptionQuery.data.activeSubscription.plan
                      .product_display_name
                  )}
                  .
                </Alert>

                <Alert severity="warning" variant="outlined">
                  All members who are not the owner will have their access to
                  hub disabled at the end of the current subscription term{" "}
                  {formatTimestamp(
                    subscriptionQuery.data.activeSubscription.renewalDate * 1000
                  )}
                  .
                </Alert>

                <ConfirmationModal
                  isLoading={undoScheduledCancellationMutation.isLoading}
                  isError={undoScheduledCancellationMutation.isError}
                  open={undoCancelModalIsOpen}
                  header={`Keep Your Subscription Active?`}
                  body={`You previously requested to cancel your subscription. By confirming here, you are cancelling that cancellation request. This means your ${
                    subscriptionQuery.data.activeSubscription.plan.display_name
                  } subscription will continue, and renew on ${formatTimestamp(
                    subscriptionQuery.data.activeSubscription.renewalDate * 1000
                  )}`}
                  leftButtonText={"Confirm"}
                  leftButtonColor="primary"
                  rightButtonText={"Cancel"}
                  rightButtonColor="warning"
                  handleLeftButton={() =>
                    undoScheduledCancellationMutation.mutate()
                  }
                  handleRightButton={() => {
                    setUndoCancelModalIsOpen(false);
                  }}
                  handleClose={() => setUndoCancelModalIsOpen(false)}
                />
              </>
            )}
            {/* Block for 'scheduled downgrade' alert */}
            {subscriptionQuery.isSuccess &&
              isSuccess &&
              subscriptionQuery.data.activeSubscription &&
              hasScheduledDowngrade && (
                <>
                  <Alert
                    severity="info"
                    action={
                      <Button
                        size="small"
                        onClick={() => {
                          futureSubscriptionMutation.reset();
                          setCancelModalIsOpen(true);
                        }}
                      >
                        Cancel scheduled change
                      </Button>
                    }
                  >
                    <AlertTitle>Scheduled plan change</AlertTitle>
                    You have a scheduled plan change from{" "}
                    {
                      subscriptionQuery.data.activeSubscription.plan
                        .display_name
                    }{" "}
                    to{" "}
                    {
                      subscriptionQuery.data.activeSubscription.nextSub!.plan
                        .display_name
                    }{" "}
                    that will take effect on the{" "}
                    {formatTimestamp(
                      subscriptionQuery.data.activeSubscription.nextSub!
                        .startTime * 1000
                    )}
                    . Until then you are still able to use all of the features
                    of{" "}
                    {toTitleCase(
                      subscriptionQuery.data.activeSubscription.plan
                        .product_display_name
                    )}
                    .
                  </Alert>

                  {subscriptionQuery.data.activeSubscription.plan.tier >
                    subscriptionQuery.data.activeSubscription.nextSub!.plan
                      .tier &&
                    !nextSubIsPermitted([PermissionsKeys.membersUpdate]) && (
                      <Alert severity="warning" variant="outlined">
                        <strong>WARNING,</strong> All members who are not the{" "}
                        <strong>owner</strong> will have their access to Hub
                        disabled at the end of the current subscription term on{" "}
                        {formatTimestamp(
                          subscriptionQuery.data.activeSubscription.nextSub!
                            .startTime * 1000
                        )}
                        .
                      </Alert>
                    )}

                  <ConfirmationModal
                    isLoading={futureSubscriptionMutation.isLoading}
                    isError={futureSubscriptionMutation.isError}
                    open={cancelModalIsOpen}
                    header={`Confirm Cancellation of Plan Change`}
                    body={`Are you sure you want to cancel the scheduled plan change? When cancelled, your previous subscription of ${
                      subscriptionQuery.data.activeSubscription.plan
                        .display_name
                    } will renew on ${formatTimestamp(
                      subscriptionQuery.data.activeSubscription.renewalDate *
                        1000
                    )}.`}
                    leftButtonText={"Confirm"}
                    leftButtonColor="primary"
                    rightButtonText={"Cancel"}
                    rightButtonColor="warning"
                    handleLeftButton={() => futureSubscriptionMutation.mutate()}
                    handleRightButton={() => {
                      setCancelModalIsOpen(false);
                    }}
                    handleClose={() => setCancelModalIsOpen(false)}
                  />
                </>
              )}

            <ConfirmationModal
              isLoading={scheduleCancellationMutation.isLoading}
              isError={scheduleCancellationMutation.isError}
              open={cancelModalIsOpen}
              header={`Confirm Cancellation`}
              body={
                <>
                  You're about to cancel your{" "}
                  <strong>
                    {
                      subscriptionQuery.data.activeSubscription.plan
                        .display_name
                    }
                  </strong>{" "}
                  subscription. Your access will continue until the end of your
                  current billing term on{" "}
                  <strong>
                    {formatTimestamp(
                      subscriptionQuery.data.activeSubscription.renewalDate *
                        1000
                    )}
                  </strong>
                  . After this date, your cancellation will be complete, and all
                  members who are not the owner will lose access to the Hub at
                  the end of the current term.
                </>
              }
              leftButtonText="Go Back"
              leftButtonColor="primary"
              rightButtonText="Cancel Subscription"
              rightButtonVariant="contained"
              rightButtonColor="error"
              handleRightButton={() => scheduleCancellationMutation.mutate()}
              handleLeftButton={() => {
                setCancelModalIsOpen(false);
              }}
              handleClose={() => setCancelModalIsOpen(false)}
            />
          </>
        )}
      <Grid container spacing={2} direction="column">
        <Grid item md={12} xs={12}>
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            <Typography variant={"h4"}>Available Plans</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={annualPlans}
                    onChange={(e) => {
                      setAnnualPlans(e.target.checked);
                      handleCancel();
                    }}
                  />
                }
                label="Pay annually"
              ></FormControlLabel>
            </FormGroup>
            <Button
              variant="contained"
              size="small"
              sx={{
                marginLeft: "auto",
              }}
              onClick={() => {
                history.push(Routes.orgSubscription);
              }}
            >
              Back
            </Button>
          </Box>
        </Grid>
        <Grid item>
          {isSuccess && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap", // allows wrapping on smaller screens
                alignItems: "stretch", // ensures equal height cards
              }}
            >
              {plansQuery.data.products
                .filter((product) => {
                  const cadence = annualPlans ? "year" : "month";
                  const price = getPlan(
                    product.productKey,
                    cadence,
                    plansQuery.data
                  );
                  const isValidPrice = !!price || !product.selfServe;
                  return isValidPrice;
                })
                .map((product) => {
                  const cadence = annualPlans ? "year" : "month";
                  const price = getPlan(
                    product.productKey,
                    cadence,
                    plansQuery.data
                  );

                  const isCurrent =
                    price?.price_lookup_id ===
                    subscriptionQuery.data?.activeSubscription?.plan
                      .price_lookup_id;

                  let type: "current" | "contact" | "trial" | "selectable";
                  if (isCurrent) {
                    type = "current";
                  } else if (!product.selfServe) {
                    type = "contact";
                  } else if (
                    canDoTrial &&
                    price?.trial_allowed &&
                    getChangeType(price, currentPlan) != "downgrade"
                  ) {
                    type = "trial";
                  } else {
                    type = "selectable";
                  }
                  return (
                    <ProductCard
                      priceId={price?.price_lookup_id ?? null} // Price can be null for non-self serve products
                      key={product.productKey}
                      title={product.displayName}
                      dollarCost={price?.dollar_cost ?? null}
                      cadence={cadence}
                      selected={selectedPrice === price?.price_lookup_id}
                      disabled={
                        price?.price_lookup_id ===
                          subscriptionQuery.data?.activeSubscription?.plan
                            .price_lookup_id &&
                        !onTrial &&
                        product.selfServe
                      }
                      type={type}
                      features={product.features}
                      handleSelect={(selected: string) => {
                        handlePriceSelection(selected);
                        setTrialSelected(type === "trial");
                      }}
                    />
                  );
                })}
            </Box>
          )}
        </Grid>
      </Grid>
      {isSuccess && (
        <Grid container spacing={2} direction="row" justifyContent="center">
          <Grid item xs={12} md={6} justifyItems="center">
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => setFeatureComparisonModalOpen(true)}
              >
                Feature Comparison
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCreditUseModalOpen(true)}
              >
                Feature Costs
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2} direction="row">
        <Grid item xs={12} md={6}>
          <CardHeader title="Overage" subheader="Set a maximum spend limit" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Overage Limit" />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Overage Limit"
                    type="number"
                    fullWidth
                    sx={{ width: "40%" }}
                    disabled={!selectedPlan || !canConfigureOverage}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {selectedPlan && (
                            <InputAdornment position="end">
                              {`/${selectedPlan.cadence}`}
                            </InputAdornment>
                          )}
                        </>
                      ),
                      inputProps: { min: 0, max: 10000 },
                    }}
                    value={overageInput}
                    onChange={(event) => {
                      setOverageInput(
                        Math.min(parseInt(event.target.value), 10000)
                      );
                      const newRoundedBudget = roundDownOverageBudget(
                        Math.min(parseInt(event.target.value), 10000),
                        {
                          overageRate: selectedPlan?.overage_cost_dollars!,
                          planCredits: selectedPlan?.credit_amount ?? 0,
                        }
                      );
                      setNewOverageBudget(newRoundedBudget);
                    }}
                  />
                </Box>
                {selectedPlan &&
                  canConfigureOverage &&
                  overageInput !== undefined &&
                  overageInput !== roundedBudget &&
                  overageInput % selectedPlan.overage_cost_dollars! !== 0 && (
                    <Alert variant="outlined" severity="info">
                      The overage limit will automatically be rounded down to
                      the nearest ${selectedPlan.overage_cost_dollars! * 1000}{" "}
                      increment the equivalent of 1000 credits (
                      {formatPrice(roundedBudget)}) when saved.
                    </Alert>
                  )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeader
            title="Coupon"
            subheader="Have a coupon code? Enter it here."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Coupon Code"
                    fullWidth
                    disabled={
                      !selectedPlan ||
                      changeType === "downgrade" ||
                      changeType === "same"
                    }
                    onChange={(e) => {
                      couponMutation.reset();
                      setCoupon(e.target.value);
                    }}
                    value={coupon}
                    id="coupon-code"
                    sx={{ width: "40%" }}
                    InputProps={{
                      endAdornment: (
                        <>
                          {couponMutation.isSuccess && couponValid && (
                            <CheckCircleIcon color="success" />
                          )}
                          {couponMutation.isError && (
                            <CancelIcon color="error" />
                          )}
                          {couponMutation.isSuccess && !couponValid && (
                            <CancelIcon color="error" />
                          )}
                        </>
                      ),
                    }}
                  />
                  <Button
                    disabled={
                      !selectedPlan ||
                      !!!coupon ||
                      changeType === "downgrade" ||
                      changeType === "same" ||
                      couponMutation.isLoading
                    }
                    onClick={() => couponMutation.mutate(coupon)}
                    size="small"
                  >
                    Apply
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      couponMutation.reset();
                      setCoupon("");
                    }}
                    disabled={
                      !selectedPlan ||
                      !!!coupon ||
                      changeType === "downgrade" ||
                      changeType === "same" ||
                      couponMutation.isLoading
                    }
                  >
                    Cancel
                  </Button>
                </Box>
                {couponMutation.isSuccess &&
                  isCouponValid(
                    selectedPlan?.price_lookup_id ?? "",
                    couponMutation.data
                  ) && (
                    <Alert variant="outlined" severity="success">
                      {generateCouponMessage(couponMutation.data)}
                    </Alert>
                  )}
                {couponMutation.isError && (
                  <Alert variant="outlined" severity="error">
                    {(couponMutation.error as ReactQueryMutationError).message}
                  </Alert>
                )}
                {couponMutation.isSuccess && !couponValid && (
                  <Alert variant="outlined" severity="error">
                    Coupon code cannot be applied to the selected subscription
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeader title="Confirm Changes" />
        </Grid>
        <Grid item xs={12} md={6}>
          <ConfirmChangesSection
            changes={getChangeSummary()}
            selectedPlan={selectedPlan}
            isLoading={subscriptionMutation.isLoading}
            canSubmit={canContinue}
            onCancel={handleCancel}
            canCancel={!!selectedPlan && !subscriptionMutation.isLoading}
            trialSelected={trialSelected}
            onSubmit={() => {
              if (!selectedPrice) {
                throw Error(
                  "Submitted subcription form without selecting a price!"
                );
              }
              subscriptionMutation.mutate({
                priceLookupId: selectedPrice,
                overageLimitDollars: canConfigureOverage ? newOverageBudget : 0,
                coupon: couponValid ? couponMutation.data?.id : undefined,
                trial: trialSelected,
              });
            }}
            newPlanIsPermitted={newPlanIsPermitted}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeader title="Cancel Subscription" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Need to take a break?" />

            <CardContent>
              <Typography variant="body1">
                Cancel your subscription at any time, you’ll lose access to hub
                features at the end of your current subscription term.
              </Typography>
            </CardContent>
            <CardActions
              sx={{
                justifyContent: "flex-end",
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={
                  !subscriptionQuery.data?.activeSubscription ||
                  hasScheduledCancellation
                }
                onClick={() => setCancelModalIsOpen(true)}
              >
                Cancel subscription
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
      <FeatureComparisonModal
        open={featureComparisonModalOpen}
        onClose={() => setFeatureComparisonModalOpen(false)}
      />
      <CreditUseModal
        open={creditUseModalOpen}
        onClose={() => setCreditUseModalOpen(false)}
      />
    </Box>
  );
};
